import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';
import {getDeviceHeaders} from '../helpers/deviceInfo';

/**
 * Self-service "logged-in devices" (sessions) API for the settings screens.
 *
 * Mirrors the auth/error conventions of userApi.js:
 *   - Bearer token from secure storage on every call (authHeaders()).
 *   - JSON responses parsed defensively (204 / empty body tolerated).
 *
 * Endpoints (backend: HappyPOS-backend src/api/controllers/user.controller.js):
 *   - GET    /api/user/me/sessions                      list the user's devices/sessions
 *   - POST   /api/user/me/sessions/{deviceId}/disable   log out a single device
 *   - DELETE /api/user/me/sessions/{deviceId}           forget (remove) a single device
 *   - POST   /api/user/me/sessions/{deviceId}/find      ping one device with a test push
 *   - POST   /api/user/me/sessions/logout-all           log out EVERY device (incl. current)
 */

const SESSIONS_URL = `${ApiConfig.apiURL}/api/user/me/sessions`;

const buildErrorFromResponse = async (response) => {
    try {
        const body = await response.json();
        const message = body?.message || body?.error || response.statusText;
        return new Error(message || `Request failed with status ${response.status}`);
    } catch (_error) {
        return new Error(response.statusText || `Request failed with status ${response.status}`);
    }
};

const parseApiResponse = async (response) => {
    if (!response.ok) {
        throw await buildErrorFromResponse(response);
    }
    if (response.status === 204) {
        return true;
    }
    const text = await response.text();
    if (!text) {
        return true;
    }
    try {
        return JSON.parse(text);
    } catch (_error) {
        return text;
    }
};

const authHeaders = async () => {
    const accessToken = await getAccessToken();
    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
    };
};

/**
 * A single device/session as returned by the backend.
 * @typedef {object} UserSession
 * @property {string} deviceId stable per-device identifier
 * @property {string} name human-readable device name
 * @property {'web'|'android'|'ios'|'terminal'|'unknown'} platform device platform
 * @property {string} [userAgent] raw user-agent string
 * @property {string} [ipAddress] last-seen IP address ("from where")
 * @property {string} registered ISO timestamp the device first signed in
 * @property {string} lastUsed ISO timestamp the device was last seen
 * @property {boolean} enabled whether the session is currently allowed to act
 * @property {boolean} current true when this is the device making the request
 */

/**
 * List the authenticated user's logged-in devices/sessions.
 * Scoped server-side to req.user._id.
 *
 * @returns {Promise<UserSession[]>} the sessions array (empty array on no data)
 */
export async function listMySessions() {
    const response = await fetch(SESSIONS_URL, {
        method: 'GET',
        headers: await authHeaders(),
    });
    const data = await parseApiResponse(response);
    return Array.isArray(data?.sessions) ? data.sessions : [];
}

/**
 * Disable (log out) a single device. If the disabled device is the current one,
 * the response's `current` flag is true and the caller must run the logout flow.
 *
 * @param {string} deviceId the device to disable
 * @returns {Promise<{success: boolean, current: boolean}>}
 */
export async function disableMySession(deviceId) {
    const response = await fetch(`${SESSIONS_URL}/${encodeURIComponent(deviceId)}/disable`, {
        method: 'POST',
        headers: await authHeaders(),
    });
    return parseApiResponse(response);
}

/**
 * Forget (remove) a single device entirely. If it is the current device, the
 * response's `current` flag is true and the caller must run the logout flow.
 *
 * @param {string} deviceId the device to remove
 * @returns {Promise<{success: boolean, current: boolean}>}
 */
export async function removeMySession(deviceId) {
    const response = await fetch(`${SESSIONS_URL}/${encodeURIComponent(deviceId)}`, {
        method: 'DELETE',
        headers: await authHeaders(),
    });
    return parseApiResponse(response);
}

/**
 * "Find this device" — send a localized test push to a single device so the
 * user can physically identify it (a phone-finder-style ping). Non-destructive.
 *
 * @param {string} deviceId the device to ping
 * @returns {Promise<{delivered: boolean, reason?: string, platform?: string}>}
 */
export async function findMySession(deviceId) {
    const response = await fetch(`${SESSIONS_URL}/${encodeURIComponent(deviceId)}/find`, {
        method: 'POST',
        headers: await authHeaders(),
    });
    return parseApiResponse(response);
}

/**
 * Log out EVERY device, including the current one. The caller must always run
 * the local logout flow afterwards.
 *
 * @returns {Promise<{success: boolean, loggedOutEverywhere: boolean}>}
 */
export async function logoutAllSessions() {
    const response = await fetch(`${SESSIONS_URL}/logout-all`, {
        method: 'POST',
        headers: await authHeaders(),
    });
    return parseApiResponse(response);
}

/**
 * Log out the CURRENT device/session on the backend.
 *
 * Calls `PUT /api/user/me/logout` with the bearer token + device headers so the
 * server marks the user logged out, clears the legacy notification token and
 * disables THIS device (matched by the `x-device-id` header) so it stops
 * receiving push notifications. Best-effort by design — the caller runs the
 * local logout (clear storage + return to Login) regardless of the result, so a
 * network/backend failure never traps the user in a signed-in state.
 *
 * @returns {Promise<{status: string}|true>} `{ status: 'loggedOut' }` on success.
 */
export async function logoutCurrentSession() {
    const [headers, deviceHeaders] = await Promise.all([
        authHeaders(),
        getDeviceHeaders().catch(() => ({})),
    ]);
    const response = await fetch(`${ApiConfig.apiURL}/api/user/me/logout`, {
        method: 'PUT',
        headers: {...headers, ...deviceHeaders},
    });
    return parseApiResponse(response);
}

export default {
    listMySessions,
    disableMySession,
    removeMySession,
    findMySession,
    logoutAllSessions,
    logoutCurrentSession,
};
